// The case types a client picks from. A code is a key: questions and
// documents name it, the matter's case type is mapped from it.

import type { IntakeGroup, IntakeLanguage, IntakeTemplateReserved } from "@duralex/shared";
import { GROUPS, move, newCategory, references, without, type Draft, rowKey } from "./draft";
import { IdField, LabelField, RowActions } from "./fields";

export function CategoriesEditor({
  draft,
  lang,
  reserved,
  existing,
  stale,
  ai,
  onChange,
}: {
  draft: Draft;
  lang: IntakeLanguage;
  reserved: IntakeTemplateReserved;
  existing: Set<string>;
  /** The keys whose `lang` translation is out of date. */
  stale: Set<string>;
  /** The keys whose `lang` cell is a model's unsaved proposal. */
  ai: Set<string>;
  onChange: (draft: Draft) => void;
}) {
  const set = (categories: Draft["categories"]) => onChange({ ...draft, categories });
  const codes = draft.categories.map((c) => c.code);
  return (
    <div className="ie-list">
      <p className="ie-hint">
        A code the matter knows (Asylum, AOS, PI...) sets the case type; a new code lands in the
        matter as &quot;other&quot; until it is mapped. Reword labels freely.
      </p>
      {draft.categories.map((c, i) => {
        const isReserved = reserved.categories.includes(c.code);
        const used = references(draft, c.code);
        return (
          <div key={rowKey(codes, i)} className="ie-row">
            <IdField
              label={`Case type code (${c.code})`}
              value={c.code}
              locked={isReserved || existing.has(`cat:${c.code}`)}
              onChange={(code) =>
                set(draft.categories.map((x, j) => (j === i ? { ...x, code } : x)))
              }
            />
            <LabelField
              label={`Case type ${c.code}`}
              text={c.label}
              lang={lang}
              stale={stale.has(`cat.${c.code}`)}
              ai={ai.has(`cat.${c.code}`)}
              onChange={(label) =>
                set(draft.categories.map((x, j) => (j === i ? { ...x, label } : x)))
              }
            />
            <select
              className="cc-field ie-select"
              aria-label={`Group of ${c.code}`}
              value={c.group}
              onChange={(e) =>
                set(
                  draft.categories.map((x, j) =>
                    j === i ? { ...x, group: e.target.value as IntakeGroup } : x,
                  ),
                )
              }
            >
              {GROUPS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
            <span className="ie-hint">{used ? `used by ${used}` : "unused"}</span>
            <RowActions
              what={`case type ${c.code}`}
              onUp={() => set(move(draft.categories, i, -1))}
              onDown={() => set(move(draft.categories, i, 1))}
              onRemove={() => set(without(draft.categories, i))}
              locked={
                isReserved
                  ? "The matter's case type reads this code"
                  : used
                    ? `Named by ${used} question(s) or document(s)`
                    : draft.categories.length === 1
                      ? "The form needs at least one case type"
                      : undefined
              }
            />
          </div>
        );
      })}
      <button
        type="button"
        className="cc-link-btn"
        onClick={() =>
          set([...draft.categories, newCategory(new Set(draft.categories.map((c) => c.code)))])
        }
      >
        Add a case type
      </button>
    </div>
  );
}
